import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Browser } from '@capacitor/browser';
import { Check, ExternalLink } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Button } from '@shared/ui/primitives/Button';
import { CalendarDatePickerField } from '@shared/ui/forms/CalendarDatePickerField';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { toast } from '@shared/ui/feedback/Toast';
import { pageCopy } from '@shared/lib/pageCopy';
import {
  clearFacilityBooking,
  cancelFacilityBooking,
  createFacilityBooking,
  fetchFacility,
  fetchFacilityAvailability,
  fetchFacilityAvailabilityMonths,
  fetchFacilityLaunch,
  fetchMyBookings,
  type FacilityAvailability,
  type FacilityBooking,
  type FacilityBookingStatus,
} from './api';
import {
  BookingDetailSheet,
  STATUS_LABEL,
  STATUS_TONE,
  type BookingSheetSlot,
} from './BookingDetailSheet';

const FULL_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

const ACTIVE_BOOKING_STATUSES = new Set<FacilityBookingStatus>(['pending', 'confirmed', 'approved']);
const VISIBLE_BOOKING_STATUSES = new Set<FacilityBookingStatus>(['pending', 'confirmed', 'approved', 'rejected']);
const CANCELLABLE_BOOKING_STATUSES = new Set<FacilityBookingStatus>(['pending', 'confirmed', 'approved']);

type DisplaySlot = BookingSheetSlot;

function canCancelBooking(booking: FacilityBooking): boolean {
  return (
    CANCELLABLE_BOOKING_STATUSES.has(booking.status) &&
    new Date(booking.starts_at).getTime() > Date.now()
  );
}

/**
 * Facility booking detail — photo header, then a date picker
 * and a grid of available time slots for the chosen day.
 */
export function FacilityDetail(): JSX.Element {
  const detailCopy = pageCopy().facilities.detail;
  const { facilityId = '' } = useParams<{ facilityId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<FacilityAvailability | null>(null);
  const [detailSlot, setDetailSlot] = useState<DisplaySlot | null>(null);
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(new Date()));

  const facilityQuery = useQuery({
    queryKey: ['facility', facilityId],
    queryFn: () => fetchFacility(facilityId),
    enabled: !!facilityId,
  });

  const facility = facilityQuery.data;
  const isInternal = facility?.booking_mode === 'internal';
  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => startOfDay(now), [now]);

  const bookingRange = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setFullYear(to.getFullYear() + 1);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const availabilityRange = useMemo(() => {
    const from = startOfMonth(visibleMonth);
    const to = startOfMonth(addMonths(visibleMonth, 1));
    return { from: from.toISOString(), to: to.toISOString() };
  }, [visibleMonth]);
  const calendarStartMonth = useMemo(() => startOfMonth(new Date()), []);
  const calendarEndMonth = useMemo(() => startOfMonth(addMonths(new Date(), 12)), []);
  const availabilityMonthsRange = useMemo(
    () => ({
      from: now.toISOString(),
      to: startOfMonth(addMonths(calendarEndMonth, 1)).toISOString(),
    }),
    [calendarEndMonth, now],
  );

  const availabilityQuery = useQuery({
    queryKey: ['facility-availability', facilityId, monthKey(visibleMonth)],
    queryFn: () => fetchFacilityAvailability(facilityId, { ...availabilityRange, is_available: true }),
    enabled: !!facilityId && isInternal,
    placeholderData: [],
    staleTime: 5 * 60 * 1000,
  });

  const availabilityMonthsQuery = useQuery({
    queryKey: ['facility-availability-months', facilityId, monthKey(calendarStartMonth), monthKey(calendarEndMonth)],
    queryFn: () => fetchFacilityAvailabilityMonths(facilityId, { ...availabilityMonthsRange, is_available: true }),
    enabled: !!facilityId && isInternal,
    staleTime: 5 * 60 * 1000,
  });

  const myBookingsQuery = useQuery({
    queryKey: ['my-bookings'],
    queryFn: fetchMyBookings,
    enabled: !!facilityId && isInternal,
  });

  const launchMutation = useMutation({
    mutationFn: () => fetchFacilityLaunch(facilityId),
    onSuccess: async (launch) => {
      if (facility?.external_open_method === 'iframe') {
        navigate(
          `/app/iframe?url=${encodeURIComponent(launch.url)}&title=${encodeURIComponent(facility?.name ?? 'Facility')}`,
        );
      } else {
        await Browser.open({ url: launch.url }).catch(() => {
          window.open(launch.url, '_blank');
        });
      }
    },
    onError: () => toast.danger('Could not open booking', 'Please try again.'),
  });

  const bookMutation = useMutation({
    mutationFn: ({ slot, attendeeCount }: { slot: FacilityAvailability; attendeeCount?: number | null }) =>
      createFacilityBooking(facilityId, {
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        attendee_count: attendeeCount ?? null,
      }),
    onSuccess: (booking) => {
      setSelected(null);
      setDetailSlot(null);
      queryClient.setQueryData<FacilityBooking[]>(['my-bookings'], (current = []) => [
        booking,
        ...current.filter((item) => item.facility_booking_id !== booking.facility_booking_id),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['facility-availability', facilityId] });
      void queryClient.invalidateQueries({ queryKey: ['facility-availability-months', facilityId] });
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success(
        booking.status === 'pending' ? 'Booking requested' : 'Booking confirmed',
        booking.approval_required ? 'Awaiting estate approval.' : 'See it under My bookings.',
      );
    },
    onError: () => toast.danger('Booking failed', 'That slot may no longer be available.'),
  });

  const cancelMutation = useMutation({
    mutationFn: (bookingId: string) => cancelFacilityBooking(bookingId),
    onSuccess: () => {
      setDetailSlot(null);
      void queryClient.invalidateQueries({ queryKey: ['facility-availability', facilityId] });
      void queryClient.invalidateQueries({ queryKey: ['facility-availability-months', facilityId] });
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success('Booking cancelled');
    },
    onError: () => toast.danger('Could not cancel', 'Please try again.'),
  });

  const clearMutation = useMutation({
    mutationFn: (bookingId: string) => clearFacilityBooking(bookingId),
    onSuccess: () => {
      setDetailSlot(null);
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success('Booking cleared');
    },
    onError: () => toast.danger('Could not clear booking', 'Please try again.'),
  });

  const confirmCancel = (booking: FacilityBooking): void => {
    if (!window.confirm('Cancel this booking?')) return;
    cancelMutation.mutate(booking.facility_booking_id);
  };

  const confirmClear = (booking: FacilityBooking): void => {
    if (!window.confirm('Clear this rejected booking?')) return;
    clearMutation.mutate(booking.facility_booking_id);
  };

  const facilityBookings = useMemo(
    () =>
      (myBookingsQuery.data ?? [])
        .filter((booking) => booking.facility_id === facilityId)
        .filter((booking) => isInRange(booking.starts_at, bookingRange.from, bookingRange.to))
        .filter((booking) => VISIBLE_BOOKING_STATUSES.has(booking.status))
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    [bookingRange.from, bookingRange.to, facilityId, myBookingsQuery.data],
  );

  const activeFacilityBookings = useMemo(
    () => facilityBookings.filter((booking) => ACTIVE_BOOKING_STATUSES.has(booking.status)),
    [facilityBookings],
  );

  /* Availability-driven days, with active bookings attached only when a slot matches. */
  const availableDays = useMemo(() => {
    const slotsByKey = new Map<string, DisplaySlot>();

    for (const slot of availabilityQuery.data ?? []) {
      if (!slot.is_available) continue;
      if (new Date(slot.starts_at).getTime() < now.getTime()) continue;
      slotsByKey.set(timeRangeKey(slot.starts_at, slot.ends_at), {
        id: slot.id,
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        availability: slot,
      });
    }

    for (const booking of activeFacilityBookings) {
      const key = timeRangeKey(booking.starts_at, booking.ends_at);
      const existing = slotsByKey.get(key);
      if (!existing) continue;
      const nextBooking = pickDisplayBooking(existing?.booking, booking);

      slotsByKey.set(key, {
        ...existing,
        booking: nextBooking,
      });
    }

    const groups = new Map<string, DisplaySlot[]>();
    for (const slot of slotsByKey.values()) {
      const key = dateKeyForIso(slot.starts_at);
      const arr = groups.get(key) ?? [];
      arr.push(slot);
      groups.set(key, arr);
    }

    return [...groups.entries()]
      .map(([key, slots]) => {
        const date = dateFromKey(key);
        return {
          key,
          date,
          slots: slots.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [availabilityQuery.data, activeFacilityBookings, now]);

  const availableDayKeySet = useMemo(
    () => new Set(availableDays.map((day) => day.key)),
    [availableDays],
  );
  const activeDay = selectedDateKey
    ? availableDays.find((d) => d.key === selectedDateKey)
    : availableDays[0];
  const selectedCalendarDate = activeDay?.date;
  const selectedSlot = selected ? toDisplaySlot(selected) : null;

  useEffect(() => {
    const availableMonths = availabilityMonthsQuery.data ?? [];
    if (availabilityMonthsQuery.isFetching || availableMonths.length === 0) return;
    if (availableMonths.includes(monthKey(visibleMonth))) return;

    const visibleYear = String(visibleMonth.getFullYear());
    const fallbackMonth = availableMonths.find((key) => key.startsWith(`${visibleYear}-`)) ?? availableMonths[0]!;
    setVisibleMonth(monthFromKey(fallbackMonth));
    setSelectedDateKey(null);
    setSelected(null);
  }, [availabilityMonthsQuery.data, availabilityMonthsQuery.isFetching, visibleMonth]);

  if (facilityQuery.isLoading) {
    return (
      <Page>
        <Skeleton className="h-44" />
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-20" />
        </div>
      </Page>
    );
  }
  if (facilityQuery.isError || !facility) {
    return (
      <Page>
        <ErrorState onRetry={() => facilityQuery.refetch()} />
      </Page>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow="Booking"
        title={facility.name}
        backTo="/app/facilities"
        backBehavior="parent"
        backgroundImage={facility.photo_url ?? undefined}
      />

      {/* ── Booking ──────────────────────────────────────────────── */}
      <div className={isInternal && selected ? 'px-6 pt-5 pb-36' : 'px-6 pt-5 pb-safe-or-6'}>
        {facility.description ? (
          <p className="mb-5 whitespace-pre-line text-body text-ink-secondary">
            {facility.description}
          </p>
        ) : null}
        {!isInternal ? (
          <Card elevated>
            <p className="eyebrow">{detailCopy.externalBooking}</p>
            <p className="mt-1 text-body text-ink-secondary">
              This facility is booked through the estate's own system.
            </p>
            <Button
              width="full"
              className="mt-4"
              loading={launchMutation.isPending}
              onClick={() => launchMutation.mutate()}
            >
              <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
              Open booking
            </Button>
          </Card>
        ) : availabilityQuery.isLoading && !availabilityQuery.data ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-28" />
          </div>
        ) : availabilityQuery.isError || availabilityMonthsQuery.isError || myBookingsQuery.isError ? (
          <ErrorState
            onRetry={() => {
              void availabilityQuery.refetch();
              void availabilityMonthsQuery.refetch();
              void myBookingsQuery.refetch();
            }}
          />
        ) : (
          <>
            {facilityBookings.length > 0 ? (
              <Card noGrain className="mb-4 bg-sunken px-4 py-3">
                <p className="mb-2 eyebrow">Your bookings</p>
                <div className="divide-y divide-hairline">
                  {facilityBookings.map((booking) => {
                    const startsAt = new Date(booking.starts_at);
                    const endsAt = new Date(booking.ends_at);
                    const canCancel = canCancelBooking(booking);
                    const canClear = booking.status === 'rejected';
                    return (
                      <div
                        key={booking.facility_booking_id}
                        className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-ink">
                            {FULL_DATE_FMT.format(startsAt)}
                          </p>
                          <p className="text-body-sm text-ink-secondary">
                            {TIME_FMT.format(startsAt)}-{TIME_FMT.format(endsAt)}
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span
                            className={
                              'inline-flex min-h-6 items-center rounded-pill border border-transparent px-2.5 py-1 text-[0.625rem] font-semibold leading-none ' +
                              STATUS_TONE[booking.status]
                            }
                          >
                            {STATUS_LABEL[booking.status]}
                          </span>
                          {canCancel ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto px-0 text-caption text-danger"
                              loading={
                                cancelMutation.isPending &&
                                cancelMutation.variables === booking.facility_booking_id
                              }
                              onClick={() => confirmCancel(booking)}
                            >
                              Cancel booking
                            </Button>
                          ) : canClear ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto px-0 text-caption text-ink-secondary"
                              loading={
                                clearMutation.isPending &&
                                clearMutation.variables === booking.facility_booking_id
                              }
                              onClick={() => confirmClear(booking)}
                            >
                              Clear
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ) : null}

            {/* Choose date */}
            <CalendarDatePickerField
              label={detailCopy.chooseDate}
              description="Change month to load availability. Days without open slots stay greyed out."
              helperText="Browse months to load availability."
              selectedDate={selectedCalendarDate}
              visibleMonth={visibleMonth}
              startMonth={calendarStartMonth}
              endMonth={calendarEndMonth}
              isLoading={availabilityQuery.isFetching}
              hasAvailableDays={availableDays.length > 0}
              emptyMonthMessage="No open slots in this month."
              enabledMonthKeys={availabilityMonthsQuery.data}
              isDateDisabled={(date) =>
                date < today || !availableDayKeySet.has(dateKeyForDate(date))
              }
              onMonthChange={(month) => {
                setVisibleMonth(startOfMonth(month));
                setSelectedDateKey(null);
                setSelected(null);
              }}
              onSelectDate={(date) => {
                setSelectedDateKey(dateKeyForDate(date));
                setSelected(null);
              }}
            />

            {/* Time slots */}
            <p className="eyebrow mb-2.5 mt-6">{detailCopy.availableTimes}</p>
            {availabilityQuery.isFetching ? (
              <div className="grid grid-cols-3 gap-2.5">
                {Array.from({ length: 12 }).map((_, index) => (
                  <Skeleton key={index} className="min-h-[52px] rounded-xl" />
                ))}
              </div>
            ) : activeDay && activeDay.slots.length > 0 ? (
              <div className="grid grid-cols-3 gap-2.5">
                {activeDay.slots.map((slot) => {
                  const booking = slot.booking;
                  const blocksBooking = !!booking && ACTIVE_BOOKING_STATUSES.has(booking.status);
                  const canOpenBooking = !!booking;
                  const canSelect = !!slot.availability && !blocksBooking && !canOpenBooking;
                  const on = !!slot.availability && selected?.id === slot.availability.id;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      disabled={!canSelect && !canOpenBooking}
                      onClick={() => {
                        if (canOpenBooking) {
                          setDetailSlot(slot);
                          return;
                        }
                        if (!slot.availability) return;
                        setSelected(on ? null : slot.availability);
                      }}
                      className={
                        'min-h-[52px] rounded-xl border px-1.5 py-2.5 text-body-sm font-medium transition-colors disabled:opacity-100 ' +
                        (on
                          ? 'border-primary-700 bg-primary-700 text-white'
                          : blocksBooking
                            ? 'border-hairline bg-sunken text-ink-secondary'
                          : 'border-hairline bg-card text-ink hover:bg-sunken')
                      }
                    >
                      <span className="block">{TIME_FMT.format(new Date(slot.starts_at))}</span>
                      {booking ? (
                        <span
                          className={
                            'mx-auto mt-1 block w-fit rounded-pill px-1.5 py-0.5 text-[0.56rem] font-semibold leading-none ' +
                            STATUS_TONE[booking.status]
                          }
                        >
                          {STATUS_LABEL[booking.status]}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-xl bg-sunken px-4 py-6 text-center text-body-sm text-ink-secondary">
                {availableDays.length === 0
                  ? 'No booking slots open this month. Choose another month.'
                  : 'No booking slots available for the selected date.'}
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Sticky confirm bar ───────────────────────────────────── */}
      {isInternal && selected ? (
        <div className="fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-40 flex items-center justify-between gap-3 border-t border-hairline bg-paper/95 px-6 py-3.5 shadow-sheet backdrop-blur">
          <div className="min-w-0 flex-1">
            <p className="text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">
              Selected
            </p>
            <p className="mt-0.5 text-body-sm font-semibold text-ink">
              {FULL_DATE_FMT.format(new Date(selected.starts_at))}
            </p>
            <p className="mt-0.5 text-caption font-medium text-ink-secondary">
              {TIME_FMT.format(new Date(selected.starts_at))}-
              {TIME_FMT.format(new Date(selected.ends_at))}
            </p>
          </div>
          <Button
            className="shrink-0"
            loading={bookMutation.isPending}
            onClick={() => {
              if (selectedSlot) {
                setDetailSlot(selectedSlot);
              }
            }}
          >
            <Check className="h-4 w-4" strokeWidth={2} />
            {facility.require_approval ? 'Request' : 'Confirm'}
          </Button>
        </div>
      ) : null}

      <BookingDetailSheet
        open={!!detailSlot}
        facility={facility}
        slot={detailSlot}
        confirmLoading={bookMutation.isPending}
        cancelLoading={cancelMutation.isPending}
        clearLoading={clearMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setDetailSlot(null);
        }}
        onConfirm={(slot, attendeeCount) => {
          if (slot.availability) {
            bookMutation.mutate({ slot: slot.availability, attendeeCount });
          }
        }}
        onClear={confirmClear}
        onCancel={confirmCancel}
      />
    </div>
  );
}

function toDisplaySlot(slot: FacilityAvailability): DisplaySlot {
  return {
    id: slot.id,
    starts_at: slot.starts_at,
    ends_at: slot.ends_at,
    availability: slot,
  };
}

function timeRangeKey(startsAt: string, endsAt: string): string {
  return `${new Date(startsAt).getTime()}-${new Date(endsAt).getTime()}`;
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dateKeyForDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function dateKeyForIso(value: string): string {
  return dateKeyForDate(new Date(value));
}

function dateFromKey(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return new Date(NaN);
  return new Date(year, month - 1, day);
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

function monthFromKey(value: string): Date {
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) return new Date(NaN);
  return new Date(year, month - 1, 1);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function isInRange(value: string, from: string, to: string): boolean {
  const time = new Date(value).getTime();
  return time >= new Date(from).getTime() && time <= new Date(to).getTime();
}

function pickDisplayBooking(
  current: FacilityBooking | undefined,
  next: FacilityBooking,
): FacilityBooking {
  if (!current) return next;

  const currentActive = ACTIVE_BOOKING_STATUSES.has(current.status);
  const nextActive = ACTIVE_BOOKING_STATUSES.has(next.status);

  if (currentActive !== nextActive) {
    return nextActive ? next : current;
  }

  return (next.created_at ?? '').localeCompare(current.created_at ?? '') > 0 ? next : current;
}
